import { format, isSameMonth, isToday } from "date-fns";
import { Loader2, Plus, X } from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Month-at-a-glance roster: a 7-column (Sun–Sat) high-density grid for one branch.
 * Clean, non-color-coded chips. Replacement (manual) staff get a sky-blue block
 * with "(แทน)"; approved leave shows as a red "<name> ลา" block (only passed in
 * for days the staff was actually scheduled).
 */
export function RosterMonthCalendar({
  loading,
  monthDate,
  days,
  getAssignmentsForCell,
  getLeavesForDate,
  onDayClick,
  onRemoveAssignment,
}) {
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-80 bg-white border border-border rounded-xl">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="roster-month-grid">
      <div className="grid grid-cols-7 bg-sand border-b border-border">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-2 py-2 text-xs font-semibold text-sage uppercase text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {weeks.map((week, wi) =>
          week.map((day) => {
            const dateStr = format(day, "yyyy-MM-dd");
            const inMonth = isSameMonth(day, monthDate);
            const today = isToday(day);
            const assignments = getAssignmentsForCell(day);
            const leaves = getLeavesForDate(day);
            const onLeaveIds = new Set(leaves.map((l) => l.staff_id));

            return (
              <div
                key={`${wi}-${dateStr}`}
                className={`min-h-[110px] border-b border-r border-border p-1.5 flex flex-col gap-1 transition-colors
                  ${inMonth ? "bg-white" : "bg-sand/30"} hover:bg-sand/50 cursor-pointer group`}
                onClick={() => onDayClick(day)}
                data-testid={`roster-day-${dateStr}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold ${today ? "bg-forest text-white rounded-full w-5 h-5 flex items-center justify-center" : inMonth ? "text-forest" : "text-sage/50"}`}>
                    {format(day, "d")}
                  </span>
                  <Plus className="h-3.5 w-3.5 text-sage opacity-0 group-hover:opacity-100" />
                </div>

                <div className="flex flex-col gap-1 overflow-hidden">
                  {assignments
                    .filter((a) => !onLeaveIds.has(a.staff_id))
                    .map((a) => {
                      const isReplacement = a.source !== "regular";
                      return (
                        <div
                          key={`${a.staff_id}-${a.source}`}
                          className={`px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                            isReplacement
                              ? "bg-sky-100 text-sky-800 border-sky-200"
                              : "bg-slate-50 text-forest border-border"
                          }`}
                          data-testid={`roster-chip-${dateStr}-${a.staff_id}`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="truncate">{a.staff_name}</span>
                            <button
                              className="opacity-0 group-hover:opacity-100 hover:text-destructive shrink-0"
                              onClick={(e) => { e.stopPropagation(); onRemoveAssignment(dateStr, a.staff_id); }}
                              data-testid={`roster-remove-${dateStr}-${a.staff_id}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                          {a.notes && <span className="block text-[10px] text-sage/90 truncate">{a.notes}</span>}
                        </div>
                      );
                    })}

                  {leaves.map((l) => (
                    <span
                      key={`leave-${l.id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-destructive/15 text-destructive truncate border border-destructive/20"
                      title={`${l.staff_name} on leave`}
                      data-testid={`roster-leave-${dateStr}-${l.staff_id}`}
                    >
                      {l.staff_name} ลา
                    </span>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
